import { cn, toRiskScore } from "@/lib/utils";
import type { RiskLevel } from "@/types";
import { StatusBadge } from "./StatusBadge";

// ─────────────────────────────────────────────
// RiskScoreCircle
//
// 140px circular gauge showing a risk score 0-100.
// Color-coded ring per RiskLevel. Score in JetBrains Mono 64px.
// Design: §9.5 Risk Score Circle
// ─────────────────────────────────────────────

interface RiskScoreCircleProps {
  score: number;
  riskLevel: RiskLevel;
  className?: string;
  /** Show risk level badge below circle (default true) */
  showBadge?: boolean;
}

const RING_COLORS: Record<RiskLevel, string> = {
  LOW: "var(--success)",
  MEDIUM: "var(--warning)",
  HIGH: "var(--k-orange)",
  VERY_HIGH: "var(--k-purple)",
  CRITICAL: "var(--danger)",
};

export function RiskScoreCircle({
  score,
  riskLevel,
  className,
  showBadge = true,
}: RiskScoreCircleProps) {
  const displayScore = toRiskScore(score);
  const size = 140;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.max(0, Math.min(100, displayScore));
  const offset = circumference - (pct / 100) * circumference;
  const ringColor = RING_COLORS[riskLevel];

  return (
    <div className={cn("flex flex-col items-center gap-3", className)}>
      {/* SVG ring */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          style={{ transform: "rotate(-90deg)" }}
        >
          {/* Track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--bg-hover)"
            strokeWidth={strokeWidth}
          />
          {/* Progress */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>

        {/* Centered score number */}
        <div
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className="font-mono font-medium leading-none"
            style={{ fontSize: 48, color: "var(--text-primary)" }}
          >
            {displayScore}
          </span>
        </div>
      </div>

      {showBadge && <StatusBadge type="risk" status={riskLevel} />}
    </div>
  );
}
